import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getApiUrl, getAuthHeaders, isUserVerified, authApi, isRecord } from '@/utils/api-config';
import { clearAllReactQueryCaches, clearUserSpecificCaches } from '@/utils/cacheUtils';
import { DEFAULT_DASHBOARD_PATH } from '@/lib/uiSwitch';

// Define user role types
export type UserRole = 'SUPERADMIN';

// Define user interface
export interface User {
  id: string;
  role?: UserRole | 'USER';
  name: string;
  email: string;
  avatar?: string;
  businessId?: string;  // Only for admin users
  accessToken?: string;
  refreshToken?: string;
  isVerified?: boolean;  // Added verification status
}

export interface Subscription {
  subscription_failed: boolean;
}

// Define auth context interface
interface AuthContextType {
  user: User | null;
  subscription?: Subscription | null;
  login: (username: string, password: string, authData?: AuthData) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  needsVerification: boolean;
  setNeedsVerification: (value: boolean) => void;
  pendingVerificationEmail: string | null;
  setPendingVerificationEmail: (email: string | null) => void;
  getToken: () => string | null;
  handleTokenExpiration: () => void;
}

interface AuthData {
  access: string;
  refresh: string;
  user_id: number;
  subscription?: Subscription | null;
  userData: {
    username: string;
    email: string;
    avatar: string | null;
    team_role: string;
    user_role: UserRole;
    permissions: Record<string, unknown>;
    is_verified: boolean;
  };
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// List of public paths that don't require authentication
const PUBLIC_PATHS = ['/login', '/verify', '/invitation'];
// Check if a path is for chat preview
const isChatPreviewPath = (path: string) => path.startsWith('/chat/preview/');
const isChatAssistant = (path: string) => path.startsWith('/chat/assistant/');
const isPasswordReset = (path: string) => path.startsWith('/reset-password');

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [needsVerification, setNeedsVerification] = useState<boolean>(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Function to get token
  const getToken = (): string | null => {
    return user?.accessToken || null;
  };

  // Handle token expiration
  const handleTokenExpiration = () => {
    console.log('Token expired, logging out user');
    
    // Show toast notification
    const event = new CustomEvent('show-toast', {
      detail: {
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        variant: 'error'
      }
    });
    window.dispatchEvent(event);
    
    // Clear all caches before updating state
    clearAllReactQueryCaches(queryClient);
    
    // Clear user data and redirect
    setUser(null);
    setIsAuthenticated(false);
    setNeedsVerification(false);
    setPendingVerificationEmail(null);
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpirationEvent = () => {
      handleTokenExpiration();
    };

    window.addEventListener('token-expired', handleTokenExpirationEvent);
    
    return () => {
      window.removeEventListener('token-expired', handleTokenExpirationEvent);
    };
  }, []);

  // Listen for token refresh events and update user state
  useEffect(() => {
    const handleTokenRefreshed = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      const detail: unknown = event.detail;
      if (!isRecord(detail) || typeof detail.newToken !== 'string') return;
      const newToken = detail.newToken;
      console.log('Token refreshed, updating AuthContext user state');
      
      // Update user state with new token
      setUser(prevUser => {
        if (prevUser) {
          return {
            ...prevUser,
            accessToken: newToken
          };
        }
        return prevUser;
      });
    };

    window.addEventListener('token-refreshed', handleTokenRefreshed);
    
    return () => {
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
    };
  }, []);

  // Proactive token checking - check every 5 minutes
  useEffect(() => {
    if (!isAuthenticated || !user?.accessToken) return;

    const checkTokenExpiration = () => {
      const user = localStorage.getItem('user');
      if (!user) return;

      try {
        const userData = JSON.parse(user);
        const token = userData.accessToken;
        
        if (!token) return;

        // Decode JWT to check expiration
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const decoded = JSON.parse(jsonPayload);
        if (!decoded || !decoded.exp) return;
        
        const currentTime = Math.floor(Date.now() / 1000);
        const expirationTime = decoded.exp;
        const timeUntilExpiration = expirationTime - currentTime;
        
        // If token expires in less than 10 minutes, log for debugging
        if (timeUntilExpiration < 600) {
          console.log('Token will expire soon, time remaining:', timeUntilExpiration, 'seconds');
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    };

    // Check immediately and then every 5 minutes
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.accessToken]);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      // If current path is a public path or chat preview, skip auth check
      if (PUBLIC_PATHS.includes(location.pathname) || isChatPreviewPath(location.pathname) || isChatAssistant(location.pathname) || isPasswordReset(location.pathname)) {
        setIsLoading(false);
        return;
      }
      
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          
          // Check if accessToken exists
          if (parsedUser.accessToken) {
            setUser(parsedUser);
            setIsAuthenticated(true);
            
            // Set verification state from stored user data
            const userNeedsVerification = parsedUser.isVerified === false;
            setNeedsVerification(userNeedsVerification);
            
            if (userNeedsVerification) {
              if (parsedUser.email) {
                setPendingVerificationEmail(parsedUser.email);
              }
              
              // Only redirect to verify if not already there and not a protected route
              if (location.pathname !== '/verify' && 
                  location.pathname !== '/login') {
                navigate('/verify');
              }
            } 
            // If user is verified and on login or verify page, redirect to dashboard
            else if (location.pathname === '/login' || location.pathname === '/verify') {
              navigate(DEFAULT_DASHBOARD_PATH);
            }
          } else {
            // Token is missing, clear storage and redirect to login
            localStorage.removeItem('user');
            navigate('/login');
          }
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          localStorage.removeItem('user');
          navigate('/login');
        }
      } else {
        // No stored user data and not on public path, redirect to login
        navigate('/login');
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, [navigate, location.pathname]);

  const login = async (username: string, password: string, authData?: AuthData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 🔒 SECURITY: Clear all caches before login to ensure fresh start
      console.log('🧹 Clearing all caches before login for security');
      clearAllReactQueryCaches(queryClient);
      
      // If authData is provided, we use it (API response)
      if (authData) {
        const isVerified = authData.userData.is_verified !== false;
        
        const userData = {
          id: authData.user_id.toString(),
          name: authData.userData.username,
          email: authData.userData.email,
          role: authData.userData.user_role,
          accessToken: authData.access,
          refreshToken: authData.refresh,
          avatar: authData.userData.avatar || `https://ui-avatars.com/api/?name=${authData.userData.username}&background=0D8ABC&color=fff`,
          isVerified: isVerified
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('subscription_status', authData.subscription ? authData.subscription.subscription_failed ? 'failed' : 'active' : 'active');
        
        // Always check verification before allowing access to dashboard
        if (!isVerified) {
          setNeedsVerification(true);
          setPendingVerificationEmail(null);
          navigate('/verify');
        }
        return;
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    console.log('🚪 User logout initiated');
    
    // 🔒 CRITICAL: Clear all React Query caches to prevent cross-user contamination
    console.log('🧹 Clearing all caches to prevent cross-user data contamination');
    clearAllReactQueryCaches(queryClient);
    
    // Clear user state
    setUser(null);
    setIsAuthenticated(false);
    setNeedsVerification(false);
    setPendingVerificationEmail(null);
    
    // Clear localStorage
    localStorage.removeItem('user');
    
    console.log('✅ User logout completed with cache clearing');
    
    // Navigate to login
    navigate('/login');
  }, [navigate, queryClient]);

  // Force logout and notify user when backend reports forbidden access
  useEffect(() => {
    const handleForbiddenAccess = () => {
      console.warn('Forbidden API response detected, logging user out');
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          title: 'Access Restricted',
          description: 'You no longer have access. Please contact support.',
          variant: 'error'
        }
      }));
      logout();
    };

    window.addEventListener('forbidden-access', handleForbiddenAccess);

    return () => {
      window.removeEventListener('forbidden-access', handleForbiddenAccess);
    };
  }, [logout]);

  const value = {
    user,
    login,
    logout,
    isLoading,
    error,
    isAuthenticated,
    needsVerification,
    setNeedsVerification,
    pendingVerificationEmail,
    setPendingVerificationEmail,
    getToken,
    handleTokenExpiration
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
